import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import {
  EventSubHttpService,
  InsightService,
} from '@sc/data-access-http-services';
import {
  Observable,
  catchError,
  of,
  switchMap,
  take,
  throwError,
  timeout,
} from 'rxjs';
import { AuthService } from '../../services';

export const DEFAULT_HTTP_TIMEOUT = new InjectionToken<number>(
  'defaultHttpTimeout'
);

const DEBUG = false;

@Injectable({
  providedIn: 'root',
})
export class AuthTokenInterceptor implements HttpInterceptor {
  constructor(
    private readonly authService: AuthService,
    private readonly insightService: InsightService,
    private readonly eventSubHttpService: EventSubHttpService,
    @Inject(DEFAULT_HTTP_TIMEOUT) private readonly defaultTimeout: number
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.handleAccess(request, next).pipe(
      catchError((err) => {
        // Error is comming from subscription service pings
        const isErrFromSubscription = request.url.includes('gqlsub');

        if (err?.status === 401) {
          // Send log to appInsight for unauthorized error
          this.authService
            .getUserLogin$()
            .pipe(take(1))
            .subscribe((userLogin) => {
              this.insightService.trackEvent({
                name: `FrontApp logout forced caused by 401 ${userLogin} on request ${request.url}`,
                properties: {
                  request,
                  err,
                },
              });
            });

          if (!isErrFromSubscription) {
            this.authService.notifyAndLogout();
          } else {
            this.authService.logout();
          }
        } else if (err?.status >= 500 && isErrFromSubscription) {
          // Force logout, if no connection with subscription service (504 Gateway Timeout for example)
          this.authService.logout();
        }

        return throwError(() => err);
      })
    );
  }

  private handleAccess(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const requestId = this.insightService.generateRequestId();
    // this.fireInsightTrackEvent(request, requestId);

    const clientKey = this.eventSubHttpService.getClientKey();

    const timeoutValue =
      parseInt(request.headers.get('timeout')) || this.defaultTimeout;

    return this.authService.getExiprationUnixMillis$().pipe(
      take(1),
      // First we check if access token is not expired and if it needs refreshing we do it
      switchMap((tokenExpireTimeMillis) => {
        const currentTime = new Date();
        const expireTokenPeriod: Date = new Date(tokenExpireTimeMillis);

        if (
          expireTokenPeriod &&
          // Test if access token will expire in less than 5 minutes
          (expireTokenPeriod.getTime() - currentTime.getTime()) / (1000 * 60) <
            5 &&
          // Test if access token was expired since less than 24 hours
          (currentTime.getTime() - expireTokenPeriod.getTime()) /
            (1000 * 3600) <
            24 &&
          !request.url.includes('token')
        ) {
          if (DEBUG) console.log('REFRESH TOKEN');

          return this.authService.forceRefreshTokens();
        }

        return of(null);
      }),
      // Then, after having a valid access token, we activate the request
      switchMap(() => {
        request = request.clone({
          setHeaders: {
            Authorization: this.authService.getHttpAuthorizationHeader(),
            requestId,
            clientKey,
          },
        });
        return next.handle(request).pipe(timeout(timeoutValue));
      })
    );
  }

  private fireInsightTrackEvent(request: HttpRequest<any>, requestId: string) {
    const requestName = request.headers.get('requestName');
    if (!this.requestInsightToIgnore(requestName)) {
      const host = request.headers.get('Host');
      this.insightService.trackEvent({
        name: `FrontApp ${host} ${requestName} start`,
        properties: {
          body: request.body,
          requestId,
        },
      });
    }
  }

  requestInsightToIgnore(requestName: string) {
    return (
      !requestName ||
      requestName === 'auth-http refreshAccessToken' ||
      requestName === 'config-http fetchConfig'
    );
  }
}
